<script lang="ts" setup>
import { useQuasar } from 'quasar';
import { useRouter } from 'vue-router';
import { useDashboardStore, useSessionStore } from 'src/store';
import { useI18n } from 'vue-i18n';
import { onMounted, computed } from 'vue';
import { handleApiRequest } from 'src/api/handleApiRequest';
import Svg from '@/components/Svg.vue';
import { calendarExclamation } from '@/assets';
import { dismissInterruptAsync, getPatientProfileData } from '@/api/endpoints/userWeb';
import { DismissInterruptType } from '@/constants/enums';

const $q = useQuasar(),
  router = useRouter(),
  sessionStore = useSessionStore(),
  dashboardStore = useDashboardStore(),
  { t } = useI18n();

const eocId = computed(() => sessionStore.user?.selectedEocId ?? 0);

const buildRequestBody = () => ({
  eocId: eocId.value,
  interruptKey: DismissInterruptType.Waitlist,
});

const dismissAndRefresh = async () => {
  $q.loading.show();
  try {
    const response = await handleApiRequest(() => dismissInterruptAsync(buildRequestBody()), null, $q);
    if (!response) return false;

    const patientResponse = await handleApiRequest(() => getPatientProfileData(true), null, $q);
    if (patientResponse) {
      dashboardStore.loadPatient(patientResponse.result);
    }
    return true;
  } finally {
    $q.loading.hide();
  }
};

const handleContinueClick = async () => {
  if (await dismissAndRefresh()) {
    await router.push({ name: 'Procedure.SetSurgeryDate' });
  }
};

const handleNoClick = async () => {
  if (await dismissAndRefresh()) {
    await router.push({ name: 'Dashboard.Home' });
  }
};

onMounted(() => {
  document.querySelectorAll('html')[0].scrollTop = 0;
  sessionStore.setIsHeaderVisible(true);
});
</script>

<template>
  <q-page class="text-center" padding>
    <zbm-box :box-bkgd-color="$zb.enums.BoxBackgroundColorEnum.linearGradient"
      :box-padding="$zb.enums.BoxPaddingEnum.p303040">

      <div class="row flex-start">
        <zbm-btn-icon @click="handleNoClick" data-testid="skipButton" icon="fa-regular fa-xmark" size="24px"
          text-color="content-grd-bg-content" />
      </div>

      <div class="col-auto">
        <!-- Shield Heart Icon -->
        <div class="calender-exclaimation-icon">
          <div class="calender-exclaimation-circle">
            <Svg :view-box="calendarExclamation.viewBox" :vector-paths="calendarExclamation.vectorPaths" />
          </div>
        </div>

        <!-- Content -->
        <h3 class="mb-30">{{ t('waiting_list_interrupt_header') }}</h3>
        <p class="last">{{ t('waiting_list_interrupt_body') }}</p>

        <!-- Continue Button -->
        <zbm-btn @click="handleContinueClick" class="glow--white" :color="$zb.enums.ButtonStdColorEnum.darkTeal"
          data-testid="continueButton" :height="$zb.enums.ButtonHeightEnum.lg" :label="t('acc_gen_continue')"
          :width="$zb.enums.ButtonWidthEnum.wFull" />
      </div>

    </zbm-box>
  </q-page>
</template>

<style lang="scss" scoped>
@use 'sass:map';

.box {
  width: 100%;
  max-width: 375px;
  margin-inline: auto;

  h2,
  h3,
  p {
    color: map.get($co-brand-clrs, content-grd-bg-content);
  }

  h3 {
    margin-block-end: 19px;
  }

  p {
    font-size: 18px;

    &.last {
      margin-bottom: 40px;
    }
  }

  // Calender Exclaimation Icon
  .calender-exclaimation-icon,
  .calender-exclaimation-circle {
    background-color: rgba($white, 0.2);
    border-radius: 50%;
  }

  .calender-exclaimation-icon {
    display: inline-block;
    border: 22px solid transparent;
    margin-bottom: 36px;

    .calender-exclaimation-circle {
      $dimension: 160px;

      width: $dimension;
      height: $dimension;
      line-height: 240px;
      color: map.get($co-brand-clrs, content-grd-bg-content);
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        width: 110px;
        height: auto;
      }
    }
  }
}
</style>
